import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, MoreHorizontal, UserMinus } from 'lucide-react'
import moment from 'moment'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import CommonTable, { type TableColumn } from '@/components/common/CommonTable'
import TablePagination from '@/components/common/TablePagination'
import CommonFilter from '@/components/common/CommonFilter'
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { RoutesLink } from '@/utility/RouteLinks'
import { ThrowError } from '@/api/callApi'
import { API_URL } from '@/api/constant'
import type { IPageInfo, IFilter } from '@/interface/common.interface'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { offboarding } from '@/interface/offboarding.interface'
import { GetFilterUrl } from '@/utility/helper'
import { useAppFilters } from '@/hooks/useAppFilters'

const OffboardingList: React.FC = () => {
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const [records, setRecords] = useState<offboarding[]>([])
    const [activeTab, setActiveTab] = useState<"All" | "Initiated" | "Completed" | "InProgress">("All")

    const {
        filterValues,
        queryString: filterQueryString,
        filterSelectorsNode,
    } = useAppFilters({
        enabled: ['branch', 'site', 'department', 'designation'],
        onChange: () => {
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
        },
    })

    const [filter, setFilter] = useState<IFilter>({
        search: "",
        status: "",
    })

    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    })

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }))
    }

    const handleSearchChange = (value: string) => {
        setFilter((prev) => {
            if (prev.search === value) return prev
            return { ...prev, search: value }
        })
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }

    const handleFilterChange = (value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }

    const handleGetOffboardings = async (
        currentPage = paginationData.currentPage,
        currentLimit = paginationData.pageSize
    ) => {
        setIsLoading(true)
        try {
            const queryParams = {
                page: currentPage,
                limit: currentLimit,
                search: filter.search || undefined,
                status: activeTab === "All" ? undefined : activeTab,
            }
            const params = GetFilterUrl(queryParams)
            const fullParams = `${params}${filterQueryString}`
            const response = await OffboardingService.getOffboardingList(fullParams)
            if (response?.status) {
                setRecords(response?.data?.offboardings || [])
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }))
            }
        } catch (error) {
            console.error("Error loading offboarding records:", error)
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleTabChange = (val: string) => {
        setActiveTab(val as "All" | "Initiated" | "Completed" | "InProgress");
        setFilter((prev) => (prev.status ? { ...prev, status: "" } : prev));
        setPaginationData((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }));
    };

    useEffect(() => {
        handleGetOffboardings(paginationData.currentPage, paginationData.pageSize)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationData.currentPage, paginationData.pageSize, filter.search, activeTab, filterValues.branchId, filterValues.siteId, filterValues.departmentId, filterValues.designationId])

    const columns: TableColumn<offboarding>[] = [
        {
            key: 'sNo',
            header: 'S.No',
            render: (_item: offboarding, index: number) => (
                <span className="text-muted-foreground font-medium text-sm">
                    {String((paginationData.currentPage - 1) * paginationData.pageSize + index + 1).padStart(2, "0")}
                </span>
            )
        },
        {
            key: 'name',
            header: 'Name',
            render: (item: offboarding) => (
                <div className="flex items-center gap-3">
                    {item?.user?.profilePhoto ? (
                        <img
                            src={API_URL + "/" + item?.user?.profilePhoto}
                            alt=""
                            className="h-10 w-10 min-w-10 rounded-full object-cover border border-slate-100 shadow-xs cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => navigate(`${RoutesLink.startOffboarding}/${item.user?.id}/${item.id}`)}
                        />
                    ) : (
                        <div
                            className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-xs cursor-pointer hover:bg-primary/20 transition-colors"
                            onClick={() => navigate(`${RoutesLink.startOffboarding}/${item.user?.id}/${item.id}`)}
                        >
                            {item?.user?.name?.charAt(0)?.toUpperCase()}
                        </div>
                    )}
                    <div>
                        <span
                            className="font-semibold text-slate-800 hover:text-primary transition-colors block text-sm cursor-pointer underline"
                            onClick={() => navigate(`${RoutesLink.startOffboarding}/${item.user?.id}/${item.id}`)}
                        >
                            {item?.user?.name}
                        </span>
                        <span className="text-xs text-muted-foreground block">
                            {item?.user?.department?.name || '—'}
                        </span>
                    </div>
                </div>
            )
        },
        {
            key: 'designation',
            header: 'Designation',
            render: (item: offboarding) => (
                <span className="text-sm font-medium text-slate-700">
                    {item?.user?.designation?.name || '--'}
                </span>
            )
        },
        {
            key: 'handoverToUser',
            header: 'Handover To',
            render: (item: offboarding) => (
                <span className="text-sm font-medium text-slate-700">
                    {item?.handoverToUser?.name || '--'}
                </span>
            )
        },
        {
            key: 'branch',
            header: 'Branch',
            render: (item: offboarding) => (
                <span className="text-sm font-medium text-slate-700">
                    {item?.user?.branch?.name || (item?.user?.site as any)?.branch?.name || '--'}
                </span>
            )
        },
        {
            key: 'site',
            header: 'Site',
            render: (item: offboarding) => (
                <span className="text-sm font-medium text-slate-700">
                    {item?.user?.site?.name || '--'}
                </span>
            )
        },
        {
            key: 'resignationDate',
            header: 'Resignation Date',
            render: (item: offboarding) => (
                <span className="text-sm text-slate-600">
                    {item?.resignationDate ? moment(item.resignationDate).format("DD-MM-YYYY") : '--'}
                </span>
            )
        },
        {
            key: 'lastWorkingDate',
            header: 'Last Working Day',
            render: (item: offboarding) => (
                <span className="text-sm text-slate-600">
                    {item?.lastWorkingDate ? moment(item.lastWorkingDate).format("DD-MM-YYYY") : '--'}
                </span>
            )
        },
        {
            key: 'relievingDate',
            header: 'Relieving Date',
            render: (item: offboarding) => (
                <span className="text-sm text-slate-600">
                    {item?.relievingDate ? moment(item.relievingDate).format("DD-MM-YYYY") : '--'}
                </span>
            )
        },
        {
            key: 'noticePeriod',
            header: 'Notice Period',
            render: (item: offboarding) => (
                <span className="text-sm font-medium text-slate-600">
                    {item?.noticePeriod || '--'}
                </span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: offboarding) => {
                let badgeClass = "bg-amber-50 text-amber-700 border-amber-200"
                if (item?.status === "Completed") badgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200"
                if (item?.status === "Initiated") badgeClass = "bg-blue-50 text-blue-700 border-blue-200"
                if (item?.status === "InProgress") badgeClass = "bg-indigo-50 text-indigo-700 border-indigo-200"
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}>
                        {item?.status || "Pending"}
                    </span>
                )
            }
        },
        {
            key: 'action',
            header: 'Action',
            render: (item: offboarding) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-slate-100 rounded-full">
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40 rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            className="cursor-pointer flex items-center gap-2 py-2 text-sm text-slate-600 hover:text-primary hover:bg-primary/5 focus:bg-primary/5 focus:text-primary rounded-lg transition-colors"
                            onClick={() => navigate(`${RoutesLink.startOffboarding}/${item.user?.id}/${item.id}`)}
                        >
                            <Pencil className="h-3.5 w-3.5 text-primary" />
                            <span>Edit Offboarding</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <MainHeading
                        title="Offboarding List"
                    />
                    <Button
                        onClick={() => navigate(RoutesLink.startOffboarding)}
                        className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-sm hover:shadow transition-all flex items-center gap-2"
                    >
                        <Plus size={16} />
                        <span>Start Offboarding</span>
                    </Button>
                </div>

                <Tabs
                    value={activeTab}
                    onValueChange={handleTabChange}
                    className="w-full"
                >
                    <TabsList className="bg-slate-100/80 flex py-5 flex-nowrap overflow-x-auto overflow-y-hidden h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner scroll-bar-hidden items-center">
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="All"
                        >
                            <UserMinus className="h-4.5 w-4.5 shrink-0" />
                            All Offboarding
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="Initiated"
                        >
                            <UserMinus className="h-4.5 w-4.5 shrink-0" />
                            Offboard Initiated
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="InProgress"
                        >
                            <UserMinus className="h-4.5 w-4.5 shrink-0" />
                            Offboard In Process
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-lg py-3.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            value="Completed"
                        >
                            <UserMinus className="h-4.5 w-4.5 shrink-0 text-green-600" />
                            Offboarding Complete
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                <CommonFilter
                    searchValue={filter.search || ""}
                    onSearchChange={handleSearchChange}
                    value={filter}
                    onChange={handleFilterChange}
                    additionalFilters={filterSelectorsNode}
                />
            </div>

            <CommonTable
                columns={columns}
                data={records}
                loading={isLoading}
            />

            {records?.length > 0 && (
                <div className="pt-6 border-t border-slate-100 mt-6">
                    <TablePagination
                        paginationData={paginationData}
                        setPaginationData={setPaginationData}
                        handleChangePageSize={handleChangePageSize}
                        isLoading={isLoading}
                    />
                </div>
            )}

        </Layout>
    )
}

export default OffboardingList
